// src/pages/Landing.jsx - Enhanced with Features and Pricing sections
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    SparklesIcon, RocketLaunchIcon, ShieldCheckIcon,
    BoltIcon, UsersIcon, ChartBarIcon, Cog6ToothIcon,
    GlobeAltIcon, CheckIcon, ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import Footer from '../components/layout/footer';
import SEO from '../components/SEO';

function Landing() {
    const location = useLocation();

    // Scroll to section based on URL
    useEffect(() => {
        const hash = location.hash;
        if (hash) {
            const element = document.getElementById(hash.substring(1));
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [location]);

    const features = [
        {
            icon: <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8" />,
            title: "AI-Powered Content Generation",
            description: "Generate engaging posts using your own OpenAI and Gemini API keys. Create compelling content in seconds that resonates with your audience.",
            details: ["Bring your own API keys", "Multiple AI models", "Custom prompts", "Brand voice matching", "Content optimization"]
        },
        {
            icon: <RocketLaunchIcon className="h-8 w-8 text-green-600" />,
            title: "Multi-Platform Publishing",
            description: "Post to LinkedIn, Bluesky, and Twitter from one dashboard. Manage all your social presence efficiently.",
            details: ["Growing with new platforms", "Simple", "Automation", "Cross-posting"]
        },
        {
            icon: <BoltIcon className="h-8 w-8 text-purple-600" />,
            title: "Smart Automation",
            description: "Schedule posts, auto-approve content, and maintain consistent posting schedules without manual intervention.",
            details: ["Smart scheduling", "Auto-approval rules", "Queue management", "Timezone optimization"]
        },
    ];

    const pricingPlans = [
        {
            name: 'Free',
            price: '$0',
            period: '1yr',
            description: 'Perfect for getting started',
            features: [
                '5 posts per month',
                '1 social platform',
                'AI generation (your API keys)',
                'Community Support',
                'Post scheduling',
                'Mobile app'
            ],
            buttonText: 'Start Free',
            buttonLink: '/register',
            popular: false
        },
        {
            name: 'Pro',
            price: '$19',
            period: 'per month',
            description: 'For growing businesses',
            features: [
                '100 posts per month',
                '2 social platforms',
                'Advanced AI features (your API keys)',
                'Post scheduling',
                'Priority support',
            ],
            buttonText: 'Start Pro Trial',
            buttonLink: '/register?plan=pro',
            popular: true
        },
        {
            name: 'Enterprise',
            price: '$99',
            period: 'per month',
            description: 'For teams and agencies',
            features: [
                '1000 posts',
                'All platforms',
                'Advanced AI models (your API keys)',
                'Custom integrations',
                'Dedicated support'
            ],
            buttonText: 'Start Enterprise Trial',
            buttonLink: '/register?plan=pro',
            popular: false
        }
    ];


    const structuredData = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "PostMgr",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "description": "AI-powered social media management platform that helps businesses create, schedule, and publish engaging content across multiple social media platforms.",
        "url": "https://postmgr.com",
        "author": {
            "@type": "Organization",
            "name": "PostMgr",
            "url": "https://postmgr.com",
            "logo": "https://postmgr.com/logo.png"
        },
        "offers": [
            {
                "@type": "Offer",
                "name": "Free Plan",
                "price": "0",
                "priceCurrency": "USD",
                "description": "5 posts per month, 1 platforms"
            },
            {
                "@type": "Offer",
                "name": "Pro Plan",
                "price": "19",
                "priceCurrency": "USD",
                "description": "100 posts per month, 2 platforms"
            }
        ],
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "150"
        }
    };

    return (
        <>
            <SEO
                title="PostMgr - AI-Powered Social Media Management Platform"
                description="Generate engaging social media posts with AI. Schedule across LinkedIn, Instagram, Bluesky, and Twitter. Start free - no credit card required."
                url="https://postmgr.com"
                structuredData={structuredData}
                keywords="social media management, AI content generation, social media automation, LinkedIn automation, Instagram scheduler, Twitter automation, Bluesky posting, social media marketing, content creation"
            />

            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
                {/* Navigation */}
                <nav className="bg-white shadow-sm sticky top-0 z-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between h-16">
                            <div className="flex items-center">
                                <Link to="/" className="flex items-center">
                                    <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8" />
                                    <span className="ml-2 text-2xl font-bold text-blue-600">PostMgr</span>
                                </Link>
                            </div>
                            <div className="hidden md:flex items-center space-x-8">
                                <a href="../#features" className="text-gray-700 hover:text-blue-600">Features</a>
                                <a href="../#pricing" className="text-gray-700 hover:text-blue-600">Pricing</a>
                                <Link to="/about" className="text-gray-700 hover:text-blue-600">About</Link>
                                <Link to="/contact" className="text-gray-700 hover:text-blue-600">Contact</Link>
                            </div>
                            <div className="flex items-center space-x-4">
                                <Link to="/login" className="text-gray-700 hover:text-blue-600">
                                    Sign In
                                </Link>
                                <Link
                                    to="/register"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Get Started
                                </Link>
                            </div>
                        </div>
                    </div>
                </nav>

                {/* Hero Section */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
                    <div className="text-center">
                        <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                            <span className="block">Automate Your</span>
                            <span className="block text-blue-600">Social Media</span>
                        </h1>
                        <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                            Generate engaging posts with AI using your own OpenAI and Gemini API keys, schedule across platforms, and grow your audience while you focus on what matters most.
                        </p>
                        <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
                            <Link
                                to="/register"
                                className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 md:py-4 md:text-lg md:px-10 transition-colors"
                            >
                                <img src="/images/Bob_only.svg" alt="PostMgr" className="h-12 w-12 mr-2" />
                                Start Free Trial
                            </Link>
                        </div>
                        <p className="mt-3 text-sm text-gray-500">
                            No credit card required • 10 posts a month for a year
                        </p>
                    </div>
                </div>

                {/* Features Section */}
                <div id="features" className="py-16 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                                Everything you need to dominate social media
                            </h2>
                            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
                                Powerful AI-driven features designed to streamline your social media workflow and maximize engagement.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {features.map((feature, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center mb-4">
                                        {feature.icon}
                                        <h3 className="ml-3 text-xl font-semibold text-gray-900">{feature.title}</h3>
                                    </div>
                                    <p className="text-gray-600 mb-4">{feature.description}</p>
                                    <ul className="space-y-2">
                                        {feature.details.map((detail, i) => (
                                            <li key={i} className="flex items-center text-sm text-gray-500">
                                                <CheckIcon className="h-4 w-4 text-green-500 mr-2" />
                                                {detail}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/*/!* Testimonials Section *!/*/}
                {/*<div className="py-16 bg-gray-50">*/}
                {/*    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">*/}
                {/*        <div className="text-center mb-16">*/}
                {/*            <h2 className="text-3xl font-extrabold text-gray-900">*/}
                {/*                Loved by thousands of marketers*/}
                {/*            </h2>*/}
                {/*            <p className="mt-4 text-xl text-gray-600">*/}
                {/*                See what our customers are saying about PostMgr*/}
                {/*            </p>*/}
                {/*        </div>*/}
                
                {/*        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">*/}
                {/*            {testimonials.map((testimonial, index) => (*/}
                {/*                <div key={index} className="bg-white rounded-lg p-6 shadow-sm">*/}
                {/*                    <p className="text-gray-600 mb-4">"{testimonial.content}"</p>*/}
                {/*                    <div className="flex items-center">*/}
                {/*                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">*/}
                {/*                            <span className="text-blue-600 font-semibold">*/}
                {/*                                {testimonial.name.split(' ').map(n => n[0]).join('')}*/}
                {/*                            </span>*/}
                {/*                        </div>*/}
                {/*                        <div className="ml-3">*/}
                {/*                            <p className="font-semibold text-gray-900">{testimonial.name}</p>*/}
                {/*                            <p className="text-sm text-gray-500">{testimonial.role}</p>*/}
                {/*                        </div>*/}
                {/*                    </div>*/}
                {/*                </div>*/}
                {/*            ))}*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*</div>*/}

                {/* Pricing Section */}
                <div id="pricing" className="py-16 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                                Simple, Transparent Pricing
                            </h2>
                            <p className="mt-4 text-xl text-gray-600">
                                Choose the plan that fits your needs. Upgrade or downgrade at any time.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {pricingPlans.map((plan, index) => (
                                <div key={index} className={`rounded-lg border-2 p-6 ${
                                    plan.popular
                                        ? 'border-blue-500 bg-blue-50 relative'
                                        : 'border-gray-200 bg-white'
                                }`}>
                                    {plan.popular && (
                                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                            <span className="bg-blue-500 text-white px-3 py-1 text-xs font-medium rounded-full">
                                                Most Popular
                                            </span>
                                        </div>
                                    )}

                                    <div className="text-center">
                                        <h3 className="text-lg font-medium text-gray-900">{plan.name}</h3>
                                        <div className="mt-4">
                                            <span className="text-4xl font-extrabold text-gray-900">{plan.price}</span>
                                            <span className="text-base font-medium text-gray-500">/{plan.period}</span>
                                        </div>
                                        <p className="mt-2 text-sm text-gray-600">{plan.description}</p>
                                    </div>

                                    <ul className="mt-6 space-y-4">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="flex items-start">
                                                <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                                                <span className="ml-3 text-sm text-gray-700">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="mt-8">
                                        <Link
                                            to={plan.buttonLink}
                                            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium ${
                                                plan.popular
                                                    ? 'text-white bg-blue-600 hover:bg-blue-700'
                                                    : 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                                            } transition-colors`}
                                        >
                                            {plan.buttonText}
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* API Key Requirements Notice */}
                        <div className="py-12 bg-blue-50">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                                <div className="bg-white rounded-lg shadow-sm border border-blue-200 p-6">
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0">
                                            <ExclamationCircleIcon className="h-6 w-6 text-blue-600" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-lg font-medium text-gray-900">
                                                Bring Your Own API Keys
                                            </h3>
                                            <div className="mt-2 text-sm text-gray-600">
                                                <p>
                                                    PostMgr requires you to provide your own OpenAI and Gemini API keys to generate content.
                                                    This ensures you have full control over your AI usage and costs while maintaining the highest
                                                    level of data privacy and security.
                                                </p>
                                                <div className="mt-3 space-y-1">
                                                    <p><strong>• OpenAI API:</strong> Required for GPT-powered content generation</p>
                                                    <p><strong>• Gemini API:</strong> Required for Google's AI content generation</p>
                                                    <p><strong>• Your keys, your control:</strong> Full transparency in AI costs and usage</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-12 text-center">
                            <p className="text-gray-600 mb-4">
                                Need something custom? Enterprise solutions available.
                            </p>
                            <Link
                                to="/contact"
                                className="text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Contact Sales →
                            </Link>

                        </div>

                    </div>
                </div>

                {/* CTA Section */}
                <div className="bg-blue-600 py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
                        <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                            Join thousands of businesses using PostMgr to automate their social media and grow their audience.
                        </p>
                        <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
                            <Link
                                to="/register"
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-colors"
                            >
                                Start Free Trial
                            </Link>
                            <Link
                                to="/contact"
                                className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-blue-500 transition-colors"
                            >
                                Contact Sales
                            </Link>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>

        </>
    );
}

export default Landing;